import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

// Contact Action Button with Direct Actions
const ContactButton = ({ type, value, colors, memberName }) => {
  const [showOptions, setShowOptions] = useState(false);
  const buttonRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Calculate tooltip position when showing
  useEffect(() => {
    if (showOptions && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 10,
        left: rect.left + rect.width / 2,
      });
    }
  }, [showOptions]);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (showOptions) {
      const handleClickOutside = () => setShowOptions(false);
      setTimeout(() => document.addEventListener('click', handleClickOutside), 100);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showOptions]);

  const handlePhoneClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowOptions(!showOptions);
  };

  const icons = {
    phone: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    email: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    instagram: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="18" cy="6" r="1.5" fill="currentColor" />
      </svg>
    ),
  };

  const phoneOptions = [
    { label: 'Ligar', href: `tel:${value}`, icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    )},
    { label: 'WhatsApp', href: `https://wa.me/${value?.replace(/\D/g, '')}`, icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    )},
    { label: 'FaceTime', href: `facetime:${value}`, icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    )},
  ];

  // Shared icon-only style: white default, illuminated on hover/active
  const iconBtnClass = "group w-11 h-11 flex items-center justify-center text-white transition-all duration-200 hover:scale-110 active:scale-95 focus:outline-none";
  const iconBtnStyle = {
    filter: 'drop-shadow(0 0 0 transparent)',
    transition: 'filter 0.2s ease, color 0.2s ease, transform 0.2s ease',
  };
  const iconHoverHandlers = {
    onMouseEnter: (e) => { e.currentTarget.style.filter = 'drop-shadow(0 0 8px rgba(147,197,253,0.9)) drop-shadow(0 0 16px rgba(59,130,246,0.6))'; },
    onMouseLeave: (e) => { e.currentTarget.style.filter = 'drop-shadow(0 0 0 transparent)'; },
    onTouchStart: (e) => { e.currentTarget.style.filter = 'drop-shadow(0 0 8px rgba(147,197,253,0.9)) drop-shadow(0 0 16px rgba(59,130,246,0.6))'; },
    onTouchEnd: (e) => { setTimeout(() => { if (e.currentTarget) e.currentTarget.style.filter = 'drop-shadow(0 0 0 transparent)'; }, 250); },
  };

  // EMAIL button - native <a> tag (no JavaScript)
  if (type === 'email') {
    return (
      <a
        href={`mailto:${value}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={iconBtnClass}
        style={iconBtnStyle}
        {...iconHoverHandlers}
        data-testid="contact-email"
      >
        {icons.email}
      </a>
    );
  }

  // INSTAGRAM button - direct link
  if (type === 'instagram') {
    const instaHandle = value?.replace('@', '') || '';
    return (
      <a
        href={`https://instagram.com/${instaHandle}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={iconBtnClass}
        style={iconBtnStyle}
        {...iconHoverHandlers}
        data-testid="contact-instagram"
      >
        {icons.instagram}
      </a>
    );
  }

  // PHONE button - simple click shows tooltip with options
  return (
    <>
      <button
        ref={buttonRef}
        onClick={handlePhoneClick}
        className={iconBtnClass}
        style={{
          ...iconBtnStyle,
          filter: showOptions
            ? 'drop-shadow(0 0 8px rgba(147,197,253,0.9)) drop-shadow(0 0 16px rgba(59,130,246,0.6))'
            : 'drop-shadow(0 0 0 transparent)',
          color: showOptions ? '#ffffff' : undefined,
        }}
        onMouseEnter={iconHoverHandlers.onMouseEnter}
        onMouseLeave={(e) => { if (!showOptions) e.currentTarget.style.filter = 'drop-shadow(0 0 0 transparent)'; }}
        data-testid="contact-phone"
      >
        {icons.phone}
      </button>
      
      {/* Phone Options Tooltip - Rendered via Portal to avoid clipping */}
      {showOptions && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="fixed z-[9999]"
            style={{ 
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: 'translateX(-50%)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Arrow pointing up */}
            <div 
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-[-5px] w-3 h-3 rotate-45"
              style={{ background: 'rgba(15,20,30,0.98)', borderTop: '1px solid rgba(255,255,255,0.15)', borderLeft: '1px solid rgba(255,255,255,0.15)' }}
            />
            <div 
              className="rounded-2xl p-2 shadow-2xl min-w-[180px] backdrop-blur-xl"
              style={{ 
                background: 'linear-gradient(145deg, rgba(20,25,40,0.98) 0%, rgba(10,15,25,0.99) 100%)',
                border: '1px solid rgba(255,255,255,0.15)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.6)'
              }}
            >
              {phoneOptions.map((opt) => (
                <a
                  key={opt.label}
                  href={opt.href}
                  onClick={() => setShowOptions(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-white/90 hover:bg-white/10 rounded-xl transition-all"
                  data-testid={`phone-option-${opt.label.toLowerCase()}`}
                >
                  <span style={{ color: colors.primary }}>{opt.icon}</span>
                  <span>{opt.label}</span>
                </a>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

// 3D Stacked Display Cards - 21st.dev Style
const DisplayCards3D = ({ members, onSelect }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Silver border for all cards, only category badge has color
  const categoryColors = {
    'diamante': { primary: '#60a5fa', secondary: '#3b82f6', badge: '#60a5fa' },
    'diamond': { primary: '#60a5fa', secondary: '#3b82f6', badge: '#60a5fa' },
    'ouro': { primary: '#94a3b8', secondary: '#64748b', badge: '#fbbf24' },
    'gold': { primary: '#94a3b8', secondary: '#64748b', badge: '#fbbf24' },
    'prata': { primary: '#94a3b8', secondary: '#64748b', badge: '#94a3b8' },
    'silver': { primary: '#94a3b8', secondary: '#64748b', badge: '#94a3b8' },
    'bronze': { primary: '#94a3b8', secondary: '#64748b', badge: '#d97706' },
    'black': { primary: '#94a3b8', secondary: '#64748b', badge: '#60a5fa' },
    'vip': { primary: '#a855f7', secondary: '#7c3aed', badge: '#a855f7' },
    'daypass': { primary: '#22c55e', secondary: '#16a34a', badge: '#22c55e' },
    'padrao': { primary: '#94a3b8', secondary: '#64748b', badge: '#60a5fa' },
  };

  const handleCardClick = (index) => {
    if (index === activeIndex) {
      // Click on active card - cycle to next
      setActiveIndex((prev) => (prev + 1) % members.length);
    } else {
      // Click on background card - bring to front
      setActiveIndex(index);
    }
  };

  if (members.length === 0) {
    return (
      <div className="text-center py-20 bg-white/[0.02] border border-white/[0.06] rounded-2xl backdrop-blur-xl">
        <img src="/w-logo.png" alt="W" className="w-16 h-16 mx-auto mb-4 opacity-30" />
        <p className="text-white/40">Nenhum socio encontrado</p>
      </div>
    );
  }

  // Reorder members so active is first, then wrap around
  const reorderedMembers = [];
  for (let i = 0; i < Math.min(members.length, 4); i++) {
    const idx = (activeIndex + i) % members.length;
    reorderedMembers.push({ ...members[idx], originalIndex: idx, stackPosition: i });
  }

  return (
    <div 
      className="relative flex items-center justify-start pl-4 py-6 overflow-visible"
      style={{ perspective: '1000px', minHeight: '380px' }}
    >
      <div 
        className="relative ml-2"
        style={{ width: '320px', height: '220px', transformStyle: 'preserve-3d' }}
      >
        {/* Render cards in reverse order so front card renders last (on top) */}
        {[...reorderedMembers].reverse().map((member) => {
          const pos = member.stackPosition;
          const isActive = pos === 0;
          const colors = categoryColors[member.cat?.toLowerCase()] || categoryColors.padrao;
          
          // 21st.dev exact effect - visible diagonal stack
          const offsetX = pos * 40;
          const offsetY = pos * 25;
          const scale = 1 - pos * 0.05;
          const opacity = 1 - pos * 0.2;
          const blur = pos > 0 ? pos * 0.8 : 0;

          return (
            <motion.div
              key={member.id}
              onClick={() => handleCardClick(member.originalIndex)}
              animate={{
                x: offsetX,
                y: offsetY,
                scale: scale,
                opacity: opacity,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute top-0 left-0 w-full cursor-pointer"
              style={{ 
                zIndex: 10 - pos,
                filter: `blur(${blur}px)`,
              }}
              data-testid={`wbusiness-card-${member.id}`}
            >
              {/* Card */}
              <div 
                className="rounded-3xl overflow-hidden backdrop-blur-xl"
                style={{ 
                  background: 'linear-gradient(145deg, rgba(26,26,31,0.98) 0%, rgba(10,10,12,0.99) 100%)',
                  border: isActive ? '1.5px solid rgba(148,163,184,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  boxShadow: isActive 
                    ? '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(148,163,184,0.15)' 
                    : '0 10px 30px rgba(0,0,0,0.3)',
                }}
              >
                {/* Animated silver border for active card */}
                {isActive && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 320 220" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id={`grad-${member.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                        <stop offset="40%" stopColor="rgba(148,163,184,0.8)" />
                        <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
                        <stop offset="60%" stopColor="rgba(100,116,139,0.8)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                    </defs>
                    <rect x="1" y="1" width="318" height="218" rx="24" fill="none" stroke={`url(#grad-${member.id})`} strokeWidth="2" strokeDasharray="1200" className="animate-card-border" />
                  </svg>
                )}

                {/* Card layout - W Business branding */}
                <div className="p-5 relative">
                  {/* Header: Logo + +Business + Category */}
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center gap-2">
                      <img src="/w-logo.png" alt="W" className="w-10 h-10" />
                      <span 
                        className="font-['Jost'] text-sm tracking-[0.15em] uppercase text-white/70"
                      >
                        BUSINESS
                      </span>
                    </div>
                    <span 
                      className="text-[10px] px-3 py-1.5 rounded-full border uppercase tracking-wider font-medium"
                      style={{ 
                        borderColor: colors.badge, 
                        color: colors.badge,
                        backgroundColor: `${colors.badge}15`
                      }}
                    >
                      {member.cat}
                    </span>
                  </div>
                  
                  {/* Name */}
                  <h3 
                    className="text-xl font-bold font-['Jost'] uppercase tracking-wide text-white mb-4"
                    style={{ textShadow: isActive ? '0 0 20px rgba(255,255,255,0.2)' : 'none' }}
                  >
                    {member.name}
                  </h3>
                  
                  {/* Contact Icons (phone, email, instagram) */}
                  <div className="flex items-center gap-3 mb-4">
                    <ContactButton 
                      type="phone" 
                      value={member.phone || '+5511999999999'} 
                      colors={{ primary: '#94a3b8', secondary: '#64748b' }}
                      memberName={member.name}
                    />
                    <ContactButton 
                      type="email" 
                      value={member.email || 'socio@wclub.com.br'} 
                      colors={{ primary: '#94a3b8', secondary: '#64748b' }}
                      memberName={member.name}
                    />
                    {member.instagram && (
                      <ContactButton 
                        type="instagram" 
                        value={member.instagram} 
                        colors={{ primary: '#94a3b8', secondary: '#64748b' }}
                        memberName={member.name}
                      />
                    )}
                    {isActive && (
                      <motion.button 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={(e) => { e.stopPropagation(); onSelect(member); }}
                        className="ml-auto w-11 h-11 flex items-center justify-center text-white/85 transition-all duration-200 hover:scale-110 hover:text-white active:scale-95 focus:outline-none"
                        style={{ transition: 'filter 0.2s ease, color 0.2s ease, transform 0.2s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.filter = 'drop-shadow(0 0 8px rgba(147,197,253,0.9)) drop-shadow(0 0 16px rgba(59,130,246,0.6))'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.filter = 'drop-shadow(0 0 0 transparent)'; }}
                        onTouchStart={(e) => { e.currentTarget.style.filter = 'drop-shadow(0 0 8px rgba(147,197,253,0.9)) drop-shadow(0 0 16px rgba(59,130,246,0.6))'; }}
                        data-testid="share-card-btn"
                        aria-label="Compartilhar"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </motion.button>
                    )}
                  </div>
                  
                  {/* Bottom: SINCE / EXP */}
                  <div className="flex justify-between text-[10px] text-white/40 uppercase tracking-wider">
                    <span>SINCE: {member.start || '2024'}</span>
                    <span>EXP: {member.valid || 'Vitalicio'}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Navigation hint */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/50 text-xs font-['Jost']">
        Toque para navegar • {activeIndex + 1} de {members.length}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes cardBorder {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -3000; }
        }
        .animate-card-border {
          animation: cardBorder 6s linear infinite;
        }
      `}</style>
    </div>
  );
};

const WBusiness = ({ members, currentMember, initialMemberId, onRefresh, isRefreshing }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('3d');
  const cardRef = useRef(null);

  // If initialMemberId is provided, auto-select that member
  useEffect(() => {
    if (initialMemberId && members.length > 0 && !selectedMember) {
      const memberToSelect = members.find(m => m.id === initialMemberId);
      if (memberToSelect) {
        setSelectedMember(memberToSelect);
      }
    }
  }, [initialMemberId, members, selectedMember]);

  const categoryColors = {
    'diamante': { primary: '#60a5fa', secondary: '#3b82f6' },
    'diamond': { primary: '#60a5fa', secondary: '#3b82f6' },
    'ouro': { primary: '#fbbf24', secondary: '#f59e0b' },
    'gold': { primary: '#fbbf24', secondary: '#f59e0b' },
    'prata': { primary: '#94a3b8', secondary: '#64748b' },
    'silver': { primary: '#94a3b8', secondary: '#64748b' },
    'bronze': { primary: '#d97706', secondary: '#b45309' },
    'black': { primary: '#60a5fa', secondary: '#3b82f6' },
    'vip': { primary: '#a855f7', secondary: '#7c3aed' },
    'daypass': { primary: '#22c55e', secondary: '#16a34a' },
    'padrao': { primary: '#60a5fa', secondary: '#3b82f6' },
  };

  const filteredMembers = members.filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Share the SELECTED MEMBER's card (not the generic WClub card)
  const shareCard = async () => {
    if (!selectedMember) {
      console.log('No member selected');
      return;
    }
    
    // Share URL uses /card/:id path which is PWA-friendly for Add to Home Screen
    // This path will redirect to the card with proper params
    const cardId = `wbc-${selectedMember.id}`; // wbc = W Business Card
    const memberUrl = `${window.location.origin}/card/${cardId}`;
    
    // Build contact info string
    let contactInfo = '';
    if (selectedMember.phone) contactInfo += `\n📞 ${selectedMember.phone}`;
    if (selectedMember.email) contactInfo += `\n📧 ${selectedMember.email}`;
    if (selectedMember.instagram) contactInfo += `\n📷 @${selectedMember.instagram.replace('@', '')}`;
    
    const shareTitle = `${selectedMember.name} - W Business Card`;
    const shareText = `Cartão W Business de ${selectedMember.name}${contactInfo}`;
    
    // iOS Safari Web Share API
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: memberUrl,
        });
        return; // Success
      } catch (err) {
        if (err.name === 'AbortError') {
          return; // User cancelled
        }
        console.log('Share failed:', err.message);
      }
    }
    
    // Fallback: Copy to clipboard
    try {
      const fullText = `${shareTitle}\n${shareText}\n\n🔗 ${memberUrl}`;
      await navigator.clipboard.writeText(fullText);
      alert('✅ Link do cartão copiado!\n\nCole em qualquer app para compartilhar.');
    } catch (clipErr) {
      // Ultra fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `${shareText}\n\n${memberUrl}`;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('✅ Link do cartão copiado!');
      } catch (e) {
        alert('Não foi possível copiar. URL: ' + memberUrl);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div 
        className="relative rounded-3xl overflow-hidden p-6 backdrop-blur-xl"
        style={{ 
          background: 'linear-gradient(145deg, rgba(10,15,30,0.95) 0%, rgba(5,10,20,0.98) 100%)',
          border: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        {/* Animated border */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
          <defs>
            <linearGradient id="wbusinessHeaderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0)" />
              <stop offset="40%" stopColor="rgba(59,130,246,0.4)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="60%" stopColor="rgba(148,163,184,0.4)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" rx="24" fill="none" stroke="url(#wbusinessHeaderGrad)" strokeWidth="1" strokeDasharray="1200" className="animate-header-border" />
        </svg>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
          <div className="flex items-center gap-4">
            <img src="/w-logo.png" alt="W" className="w-12 h-12 object-contain" />
            <div>
              <h2 className="text-2xl font-bold font-['Jost'] text-white/90">W Business</h2>
              <p className="text-white/40 text-sm">Compartilhe seu cartao de socio</p>
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="px-4 py-2 bg-white/[0.05] border border-white/[0.1] rounded-xl text-sm text-white/60 outline-none cursor-pointer"
              data-testid="view-mode-select"
            >
              <option value="3d">3D Cards</option>
              <option value="grid">Grid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Search + Refresh */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Buscar socio por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-5 py-3.5 pl-12 bg-white/[0.03] border border-white/[0.08] rounded-2xl text-white/90 placeholder:text-white/30 outline-none focus:border-blue-500/50 transition"
            data-testid="wbusiness-search"
          />
          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
        </div>
        {onRefresh && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onRefresh}
            disabled={isRefreshing}
            className="px-3 py-3.5 text-white/70 hover:text-white transition disabled:opacity-50"
            data-testid="wbusiness-refresh"
            title="Atualizar lista de sócios"
          >
            <svg 
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
          </motion.button>
        )}
      </div>

      {/* Members Display */}
      {viewMode === '3d' ? (
        <DisplayCards3D 
          members={filteredMembers} 
          onSelect={setSelectedMember}
        />
      ) : (
        // Grid View
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredMembers.map((member) => {
            const colors = categoryColors[member.cat?.toLowerCase()] || categoryColors.padrao;
            
            return (
              <motion.div
                key={member.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedMember(member)}
                className="relative rounded-2xl p-4 cursor-pointer backdrop-blur-xl transition-all"
                style={{ 
                  background: 'linear-gradient(145deg, rgba(15,20,35,0.9) 0%, rgba(10,15,25,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <div 
                  className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden ring-2"
                  style={{ ringColor: colors.primary }}
                >
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center text-2xl font-bold"
                      style={{ background: `linear-gradient(135deg, ${colors.primary}30, ${colors.secondary}30)`, color: colors.primary }}
                    >
                      {member.name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <h3 className="text-center text-white/90 font-['Jost'] font-medium text-sm truncate">{member.name}</h3>
                <div className="mt-2 flex justify-center">
                  <span 
                    className="px-2 py-0.5 text-[10px] rounded-full uppercase tracking-wider"
                    style={{ backgroundColor: `${colors.primary}20`, color: colors.primary, border: `1px solid ${colors.primary}40` }}
                  >
                    {member.cat}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Card Preview Modal - Member Card Style */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm"
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedMember(null)}
                className="absolute -top-12 right-4 p-2 text-white/50 hover:text-white/80 transition z-20"
                data-testid="close-modal-btn"
              >
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Member Card Style with FUNCTIONAL buttons */}
              <div 
                ref={cardRef}
                className="relative rounded-2xl overflow-hidden"
                style={{ 
                  background: 'linear-gradient(145deg, rgba(26,26,31,0.98) 0%, rgba(10,10,12,0.99) 100%)',
                }}
              >
                {(() => {
                  const colors = categoryColors[selectedMember.cat?.toLowerCase()] || categoryColors.padrao;
                  
                  return (
                    <>
                      {/* Animated Border */}
                      <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 400 240" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="businessCardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                            <stop offset="40%" stopColor={`${colors.primary}99`} />
                            <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
                            <stop offset="60%" stopColor={`${colors.secondary}99`} />
                            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                          </linearGradient>
                        </defs>
                        <rect x="1" y="1" width="398" height="238" rx="16" ry="16" fill="none" stroke="url(#businessCardGrad)" strokeWidth="2" strokeDasharray="1200" className="business-border-anim" />
                      </svg>

                      {/* Card Content - W Business Layout */}
                      <div className="p-6 relative">
                        {/* Header: Logo + +BUSINESS + Category */}
                        <div className="flex justify-between items-start mb-6">
                          <div className="flex items-center gap-2">
                            <img src="/w-logo.png" alt="W" className="w-10 h-10" />
                            <span 
                              className="font-['Jost'] text-sm tracking-[0.15em] uppercase"
                              style={{ color: colors.primary }}
                            >
                              BUSINESS
                            </span>
                          </div>
                          <span 
                            className="text-[10px] px-3 py-1.5 rounded-full border uppercase tracking-wider font-medium"
                            style={{ 
                              borderColor: colors.primary, 
                              color: colors.primary,
                              backgroundColor: `${colors.primary}10`
                            }}
                          >
                            {selectedMember.cat}
                          </span>
                        </div>
                        
                        {/* Name */}
                        <h3 className="text-xl font-bold font-['Jost'] uppercase tracking-wide text-white mb-4">
                          {selectedMember.name}
                        </h3>
                        
                        {/* Contact Icons (phone, email, instagram) */}
                        <div className="flex items-center gap-3 mb-4">
                          <ContactButton 
                            type="phone" 
                            value={selectedMember.phone || '+5511999999999'} 
                            colors={colors}
                            memberName={selectedMember.name}
                          />
                          <ContactButton 
                            type="email" 
                            value={selectedMember.email || 'socio@wclub.com.br'} 
                            colors={colors}
                            memberName={selectedMember.name}
                          />
                          {selectedMember.instagram && (
                            <ContactButton 
                              type="instagram" 
                              value={selectedMember.instagram} 
                              colors={colors}
                              memberName={selectedMember.name}
                            />
                          )}
                        </div>
                        
                        {/* Bottom: SINCE / EXP */}
                        <div className="flex justify-between text-[10px] text-white/40 uppercase tracking-wider">
                          <span>SINCE: {selectedMember.start || '2024'}</span>
                          <span>EXP: {selectedMember.valid || 'Vitalicio'}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Share Button */}
              <div className="mt-4">
                <button
                  onClick={shareCard}
                  className="w-full py-4 rounded-2xl text-sm font-['Jost'] transition-all flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(37,99,235,0.3) 100%)',
                    border: '1px solid rgba(59,130,246,0.4)',
                    color: '#60a5fa',
                    boxShadow: '0 0 30px rgba(59,130,246,0.2)'
                  }}
                  data-testid="share-card-final-btn"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Compartilhar Cartao
                </button>
              </div>
              
              <p className="text-center text-white/30 text-xs mt-3">
                O link compartilhado pode ser salvo na tela inicial
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global styles */}
      <style>{`
        @keyframes headerBorder {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -2400; }
        }
        .animate-header-border {
          animation: headerBorder 10s linear infinite;
        }
        @keyframes businessBorder {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -2400; }
        }
        .business-border-anim {
          animation: businessBorder 6s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default WBusiness;
